<template>
  <div>
    <secondHeader />
    <div class="rt-main">
      <sidebar />
      <div class="rt-content-wrapper">
        <div class="rt-page-header">
          <h1 class="rt-page-title">反射神経テスト</h1>
          <p class="rt-page-description">
            画面が<span class="rt-highlight-green">緑色</span>に変わったら素早くクリック！<br />
            5回のチャレンジで、あなたの平均反射神経を測定します。
          </p>
        </div>
        <div class="rt-reaction-test-container">
          <div class="rt-test-wrapper">
            <!-- 状態: 開始待ち -->
            <div
              v-if="state === 'waiting'"
              class="rt-test-area rt-is-waiting rt-pulsing"
              @click="prepare"
            >
              <div class="rt-content">
                <h1>クリックして開始</h1>
              </div>
            </div>

            <!-- 状態: 準備中 -->
            <div v-if="state === 'ready'" class="rt-test-area rt-is-ready" @click="handleFlying">
              <div class="rt-content">
                <h1>...</h1>
                <p class="rt-progress">{{ currentAttempt }} / 5</p>
              </div>
            </div>

            <!-- 状態: 計測中 -->
            <div v-if="state === 'measuring'" class="rt-test-area rt-is-measuring" @click="measure">
              <div class="rt-content">
                <h1>クリック！</h1>
                <p class="rt-progress">{{ currentAttempt }} / 5</p>
              </div>
            </div>
            
            <!-- 状態: ラウンド結果表示 -->
            <div v-if="state === 'round_result'" class="rt-test-area rt-is-round-result">
                <div class="rt-content">
                    <p class="rt-last-result">{{ results[results.length - 1] }} ms</p>
                    <p class="rt-progress">{{ currentAttempt }} / 5</p>
                </div>
            </div>

            <!-- 状態: フライング -->
            <div v-if="state === 'flying'" class="rt-test-area rt-is-flying">
                <div class="rt-content">
                    <h1>フライング！</h1>
                    <p class="rt-progress">{{ currentAttempt }} / 5</p>
                </div>
            </div>

            <!-- 状態: 最終結果表示 -->
            <div v-if="state === 'result'" class="rt-test-area rt-is-result">
              <div class="rt-result-card">
                <h2>平均スコア</h2>
                <p class="rt-score">{{ averageResult }} ms</p>
                <p class="rt-rank" :style="{ color: rank.color }">
                  ランク: {{ rank.name }}
                </p>
                <p class="rt-comment">{{ rank.comment }}</p>
                <p class="rt-trivia">豆知識: 人間の平均反応速度は200〜250msだよ！</p>
                <div class="rt-results-list">
                  <span v-for="(res, index) in results" :key="index" class="rt-result-item">
                    {{ index + 1 }}回目: {{ res }} ms
                  </span>
                </div>
              </div>
            </div>

            <div v-if="state === 'result'" class="rt-button-container">
              <button @click="reset" class="rt-reset-button">もう一度挑戦</button>
              <button @click="share" class="rt-tweet-button">
                結果をシェアする
              </button>
            </div>
            <div class="ad-block">
                <div class="ad">
                <InFeedAdsense
                    data-ad-layout-key="-fb+5w+4e-db+86"
                    data-ad-client="ca-pub-9016794589237931"
                    data-ad-slot="4898735474">
                </InFeedAdsense>
                </div>
            </div>
          </div>
          <!-- 右側の結果蓄積サイドバー -->
          <div class="rt-results-sidebar" :class="{ 'rt-is-final-result': state === 'result' }">
            <h2 class="rt-results-title">あなたの記録</h2>
            <div class="rt-round-results-list">
              <div v-if="results.length === 0" class="rt-no-results">
                ここに記録が表示されます
              </div>
              <div v-else v-for="(res, index) in results" :key="index" class="rt-result-badge">
                <span>{{ index + 1 }}回目</span>
                <strong>{{ res }} ms</strong>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import secondHeader from "../components/secondHeader.vue";
import sidebar from "../components/sidebar.vue";

export default {
  name: "ReactionTest",
  components: {
    secondHeader,
    sidebar,
  },
  data() {
    return {
      state: "waiting", // waiting, ready, measuring, round_result, flying, result
      startTime: null,
      timeoutId: null,
      rafId: null, // requestAnimationFrame ID
      results: [],
      currentAttempt: 0,
      averageResult: 0,
    };
  },
  computed: {
    stateClass() {
      return `is-${this.state}`;
    },
    rank() {
      if (this.averageResult === 0) return {};

      const ranks = {
        "S+": {
          name: "S+",
          comments: [
            "神の領域！もはや機械の域です！",
            "光の速さ！人間やめてますか？",
            "見えましたか…？今のクリック。",
          ],
          color: "#ff4757",
        },
        S: {
          name: "S",
          comments: [
            "超人的な反射神経！プロゲーマー級です！",
            "あなたの集中力、凄まじいですね！",
            "まさに電光石火！お見事です！",
          ],
          color: "#ffa502",
        },
        A: {
          name: "A",
          comments: [
            "素晴らしい！平均よりかなり速いです！",
            "かなりの実力者ですね！自信を持ってOK！",
            "鋭い反応！素晴らしいです！",
          ],
          color: "#2ed573",
        },
        B: {
          name: "B",
          comments: [
            "平均的な反応速度です。良いですね！",
            "ナイス反応！安定感があります。",
            "標準的なスピード。まだまだ伸びしろアリ！",
          ],
          color: "#1e90ff",
        },
        C: {
          name: "C",
          comments: [
            "平均より少しゆっくりめです。",
            "リラックスしてもう一度挑戦してみては？",
            "お疲れですか？コーヒーでもどうぞ☕",
          ],
          color: "#7f8fa6",
        },
        D: {
          name: "D",
          comments: [
            "のんびり屋さんかな？リラックスしていきましょう！",
            "マイペースが一番！大丈夫です！",
            "もしかして考え事してました？",
          ],
          color: "#a4b0be",
        },
      };

      let resultRank;
      if (this.averageResult <= 120)       resultRank = ranks["S+"];
      else if (this.averageResult <= 150) resultRank = ranks["S"];
      else if (this.averageResult <= 200) resultRank = ranks["A"];
      else if (this.averageResult <= 250) resultRank = ranks["B"];
      else if (this.averageResult <= 300) resultRank = ranks["C"];
      else                                resultRank = ranks["D"];
      
      const randomIndex = Math.floor(Math.random() * resultRank.comments.length);
      const selectedComment = resultRank.comments[randomIndex];

      return {
        name: resultRank.name,
        comment: selectedComment,
        color: resultRank.color,
      };
    },
  },
  methods: {
    startReadyTimer() {
        this.state = "ready";
        const delay = Math.random() * 2000 + 1000; // 1〜3秒のランダムな遅延
        
        this.timeoutId = setTimeout(() => {
            // requestAnimationFrameを使用して、描画とタイマー開始を同期
            this.rafId = requestAnimationFrame(() => {
                this.state = "measuring";
                this.startTime = performance.now();
            });
        }, delay);
    },
    handleFlying() {
      clearTimeout(this.timeoutId);
      cancelAnimationFrame(this.rafId); // スケジュールされた描画をキャンセル
      this.state = 'flying';
      this.timeoutId = setTimeout(this.startReadyTimer, 1500); // 1.5秒後に同じラウンドを再試行
    },
    prepare() {
      if (this.currentAttempt >= 5) return;
      this.currentAttempt++;
      this.startReadyTimer();
    },
    measure() {
      const endTime = performance.now();
      const result = Math.round(endTime - this.startTime);
      this.results.push(result);

      if (this.currentAttempt < 5) {
        this.state = "round_result";
        this.timeoutId = setTimeout(this.prepare, 1500); // 1.5秒後に次のラウンドへ
      } else {
        this.averageResult = Math.round(
          this.results.reduce((a, b) => a + b, 0) / this.results.length
        );
        this.state = "result";
      }
    },
    reset() {
      clearTimeout(this.timeoutId);
      cancelAnimationFrame(this.rafId);
      this.state = "waiting";
      this.startTime = null;
      this.timeoutId = null;
      this.rafId = null;
      this.results = [];
      this.currentAttempt = 0;
      this.averageResult = 0;
    },
    share() {
      const text = `私の反射神経は【${this.averageResult}ms】で、ランクは【${this.rank.name}】でした！\nみんなで挑戦しよう！`;
      const url = window.location.href;
      const hashtags = "はいしんツールキット,反射神経テスト";
      const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
        text
      )}&url=${encodeURIComponent(url)}&hashtags=${encodeURIComponent(hashtags)}`;
      window.open(twitterUrl, "_blank");
    },
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=M+PLUS+Rounded+1c:wght@400;700&display=swap");

.rt-main {
  display: flex;
  flex-direction: row;
  min-height: 100vh;
}

.rt-content-wrapper {
  flex-grow: 1;
  padding: 40px 20px;
  background-color: #f0f4f8;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
}

.rt-page-header {
  text-align: center;
  margin-bottom: 30px;
  max-width: 800px;
}

.rt-page-title {
  font-size: 2.2rem;
  font-weight: 700;
  color: #3d3d3d;
  margin-bottom: 10px;
}

.rt-page-description {
  font-size: 1.1rem;
  color: #596275;
  line-height: 1.6;
}

.rt-page-description .rt-highlight-green {
  color: #2ed573;
  font-weight: bold;
}

.rt-reaction-test-container {
  display: flex;
  align-items: flex-start;
  gap: 30px;
  font-family: "M PLUS Rounded 1c", sans-serif;
  color: #596275;
  width: 100%;
  max-width: 1100px;
  margin: 0 auto;
}

.rt-test-wrapper {
  flex: 1;
  text-align: center;
  max-width: 800px;
}

.rt-results-sidebar {
  width: 240px;
  flex-shrink: 0;
  background-color: white;
  border-radius: 20px;
  padding: 20px;
  border: 3px solid #e0e4e8;
  transition: opacity 0.3s ease;
}

.rt-results-sidebar.rt-is-final-result {
    opacity: 0;
    pointer-events: none;
}

.rt-results-title {
  text-align: center;
  font-size: 1.5rem;
  font-weight: 700;
  color: #3d3d3d;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 3px solid #f0f4f8;
}

.rt-round-results-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.rt-no-results {
  color: #a4b0be;
  text-align: center;
  font-size: 0.9rem;
  padding: 20px 0;
}

.rt-result-badge {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #f0f4f8;
  padding: 10px 15px;
  border-radius: 12px;
  font-size: 1rem;
  color: #596275;
  font-weight: bold;
  border: 2px solid #e0e4e8;
}

.rt-result-badge strong {
  color: #ff6b81;
}

.rt-test-area {
  width: 100%;
  min-height: 450px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border-radius: 30px; /* 角を大きく丸める */
  cursor: pointer;
  user-select: none;
  padding: 1rem;
  box-sizing: border-box;
  position: relative;
  border: 4px solid #dcdde1;
  transition: background-color 0.1s linear, border-color 0.1s linear;
}

.rt-progress {
    position: absolute;
    bottom: 20px;
    right: 30px;
    font-size: 1.2rem;
    font-weight: bold;
    color: rgba(255, 255, 255, 0.7);
}

.rt-last-result {
    font-size: 3rem;
    font-weight: 700;
    color: white;
}

.rt-content {
  text-align: center;
}

h1 {
  font-size: 2.5rem;
  font-weight: 700;
  color: white;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* 状態ごとのスタイル */
.rt-test-area.rt-is-waiting {
  background-color: #ffc0cb; /* パステルピンク */
  border-color: #ff9aab;
}

.rt-test-area.rt-is-round-result {
  background-color: #87cefa; /* フィードバック用の青 */
  border-color: #1e90ff;
  cursor: default;
}

.rt-test-area.rt-is-flying {
  background-color: #ff7675; /* 警告用の赤 */
  border-color: #d63031;
  cursor: default;
}

@keyframes pulse {
  0% { transform: scale(1); }
  50% { transform: scale(1.01); }
  100% { transform: scale(1); }
}

.rt-test-area.rt-pulsing h1 {
  animation: pulse 2s infinite ease-in-out;
}

.rt-test-area.rt-is-ready {
  background-color: #fbc5c5; /* 少し濃いピンク（待機） */
  border-color: #f7a4a4;
}

.rt-test-area.rt-is-measuring {
  background-color: #2ed573; /* パステルグリーン（クリックOK） */
  border-color: #25b864;
}

.rt-test-area.rt-is-result {
  background-color: transparent;
  border-color: transparent;
  cursor: default;
}

.rt-result-card {
  background-color: white;
  padding: 2rem 3rem;
  border-radius: 20px;
  max-width: 100%;
  margin: 0 auto;
  border: 4px solid #ffc0cb;
  animation: result-appear 0.5s ease-out;
}

@keyframes result-appear {
    from {
        opacity: 0;
        transform: translateY(20px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}


h2 {
  color: #3d3d3d;
  margin-bottom: 1rem;
}

.rt-score {
  font-size: 3.5rem;
  font-weight: 700;
  color: #ff6b81;
  margin-bottom: 0.5rem;
}

.rt-rank {
  font-size: 1.8rem;
  font-weight: bold;
  margin-bottom: 1rem;
}

.rt-comment {
  font-size: 1rem;
  color: #596275;
  margin-bottom: 1.5rem;
}

.rt-trivia {
  font-size: 0.9rem;
  color: #a4b0be;
  margin-bottom: 1.5rem;
  padding: 0.5em 1em;
  border: 2px solid #f0f4f8;
  border-radius: 10px;
  display: inline-block;
}

.rt-results-list {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 10px;
    padding-top: 1rem;
    border-top: 2px solid #f0f4f8;
}

.rt-result-item {
    background-color: #f0f4f8;
    padding: 5px 10px;
    border-radius: 10px;
    font-size: 0.9rem;
    border: 2px solid #e0e4e8;
}

.rt-button-container {
    display: flex;
    justify-content: center;
    gap: 1rem;
    margin-top: 2rem;
}

.rt-reset-button, .rt-tweet-button {
  padding: 1rem 1.5rem;
  border: 0;
  border-radius: 50px;
  color: white;
  font-size: 1.2rem;
  font-weight: 700;
  font-family: "M PLUS Rounded 1c", sans-serif;
  cursor: pointer;
  transition: transform 0.1s ease;
  border-bottom: 4px solid rgba(0, 0, 0, 0.2);
}

.rt-reset-button:active, .rt-tweet-button:active {
    transform: translateY(2px);
    border-bottom-width: 2px;
}

.rt-reset-button {
  background-color: #ff6b81;
}

.rt-tweet-button {
  background-color: #1DA1F2;
}

@media screen and (max-width: 960px) {
  .rt-reaction-test-container {
    flex-direction: column;
    align-items: center;
  }
  .rt-test-wrapper {
    width: 100%;
  }
  .rt-results-sidebar {
    width: 100%;
    order: -1; /* 結果表示をテストエリアの上に移動 */
    margin-bottom: 20px;
  }
  .rt-round-results-list {
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: center;
  }
}

@media screen and (max-width: 599px) {
  h1 {
    font-size: 1.8rem;
  }
  .rt-result-card {
    padding: 1.5rem;
  }
  .rt-score {
    font-size: 2.5rem;
  }
  .rt-rank {
    font-size: 1.5rem;
  }
  .rt-comment {
    font-size: 0.9rem;
  }
  .rt-reset-button, .rt-tweet-button {
    font-size: 1rem;
    padding: 0.8rem 1.5rem;
  }
  .rt-button-container {
      flex-direction: column;
  }
}
</style> 